import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faCircleExclamation,
  faTriangleExclamation,
  faCircleInfo,
  faCircleCheck,
} from "@fortawesome/pro-regular-svg-icons";
import MuiAlert, { AlertColor, AlertProps as MuiAlertProps } from "@mui/material/Alert";
import MuiAlertTitle from "@mui/material/AlertTitle";
import Stack from "@mui/material/Stack";
import { ReactElement, ReactNode, useMemo } from "react";
import { FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE, FaIcon } from "../../DataDisplay/Icon/FaIcon";

const severityIconMap: Record<AlertColor, IconDefinition> = {
  error: faCircleExclamation,
  warning: faTriangleExclamation,
  info: faCircleInfo,
  success: faCircleCheck,
};

export type SrAlertSize = "medium" | "large" | "xlarge";

export type SrAlertProps = Omit<MuiAlertProps, "title"> & {
  size?: SrAlertSize;
  title?: ReactNode;
};

export const SrAlert = ({
  children,
  icon: iconProp,
  size = "medium",
  sx,
  severity,
  title,
  ...props
}: SrAlertProps): ReactElement => {
  const largeIcon = useMemo((): ReactNode => {
    if (size === "medium") {
      return undefined;
    }
    if (iconProp) {
      return iconProp;
    }
    if (iconProp === false) {
      return undefined;
    }
    return (
      <FaIcon
        icon={(severity ? severityIconMap[severity as AlertColor] : undefined) ?? faCircleCheck}
        fontSize="medium"
      />
    );
  }, [iconProp, severity, size]);

  return (
    <MuiAlert
      {...props}
      severity={severity}
      icon={size === "medium" || !title ? iconProp : false}
      sx={[
        {
          ".MuiAlert-icon, .MuiAlertTitle-root": {
            [FA_ICON_DEFAULT_FONT_SIZE_CSS_VARIABLE]: "1em",
          },
        },
        ...(size === "large"
          ? [
              {
                borderRadius: 0.5,
                px: { xs: 2, md: 4 },
                py: { xs: 3, md: 4 },
                ".MuiAlert-message": {
                  typography: "body1",
                },
                ".MuiAlertTitle-root": {
                  typography: "h3",
                },
              },
            ]
          : []),
        ...(size === "xlarge"
          ? [
              {
                borderRadius: 0.5,
                px: { xs: 2, md: 4 },
                py: { xs: 3, md: 4 },
                ".MuiAlert-message": {
                  typography: "subtitle1",
                },
                ".MuiAlertTitle-root": {
                  typography: "h1",
                },
                ".MuiAlert-action": {
                  pt: 0.75,
                },
              },
            ]
          : []),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {title && (
        <MuiAlertTitle sx={{ mb: children ? 1 : 0 }}>
          {largeIcon ? (
            <Stack direction="row" gap={1.5} alignItems="center">
              {largeIcon}
              {title}
            </Stack>
          ) : (
            title
          )}
        </MuiAlertTitle>
      )}
      {children}
    </MuiAlert>
  );
};
